import * as Sentry from '@sentry/react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Context, UserProfile } from '~/prix'
import { APIProvider, API } from '~/prix/react/hooks/api'
import userSelfDefinition from '~/packages/general/user/self/userSelf.definition'
import { bypassAuth } from '~/utils/shared/platform'
import { auth } from '~/utils/client/auth'
import type { User as FirebaseUser } from 'firebase/auth'
import { bypassUserProfile } from '~/utils/shared/bypassUserProfile'

const date = new Date()
const userAgent = typeof navigator === 'undefined' ? null : navigator.userAgent
const contextBase = {
  ip: '127.0.0.1',
  userAgent,
  timezoneOffset: date.getTimezoneOffset(),
  bypassAuth,
  requestId: null,
  userLogHelper: null,
} as const

function areUserProfilesEqual(first: UserProfile | null, second: UserProfile | null) {
  if (first === second) {
    return true
  }
  if (!first || !second) {
    return false
  }

  return JSON.stringify(first) === JSON.stringify(second)
}

export default function ApiWithContext({
  children,
  sources,
}: Omit<API, 'isLoading' | 'context'> & { children: React.ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [token, setToken] = useState<string | null>(null)
  const requestRef = useRef(0)

  const getToken = useCallback(async () => {
    const currentUser = auth.currentUser
    if (!currentUser) {
      return null
    }

    return currentUser.getIdToken()
  }, [])

  const setStableUser = useCallback((nextUser: UserProfile | null) => {
    setUser(previousUser => (areUserProfilesEqual(previousUser, nextUser) ? previousUser : nextUser))
  }, [])

  useEffect(() => {
    return auth.onIdTokenChanged(user => {
      if (user) {
        user
          .getIdToken()
          .then(nextToken =>
            setToken(currentToken => (currentToken === nextToken ? currentToken : nextToken)),
          )
          .catch(error => {
            console.error(error)
            Sentry.captureException(error)
            setToken(currentToken => (currentToken === null ? currentToken : null))
          })
      } else {
        setToken(currentToken => (currentToken === null ? currentToken : null))
      }
    })
  }, [])

  const updateAuthUser = useCallback(
    async (currentUser: FirebaseUser | null) => {
      const requestId = ++requestRef.current

      if (!currentUser && !bypassAuth) {
        setStableUser(null)
        setIsLoading(false)
        return
      }

      setIsLoading(true)

      try {
        if (bypassAuth) {
          setStableUser(bypassUserProfile)
          return
        }

        const source =
          sources.default ?? Object.values(sources).find(currentSource => Boolean(currentSource))
        if (!source) {
          throw new Error('Nenhuma fonte de dados disponível para buscar /user/self')
        }

        const { run } = source.call(
          userSelfDefinition,
          {},
          {
            ...contextBase,
            token: null,
            getToken,
            user: null,
          },
        )
        const result = (await run()) as {
          userProfile?: UserProfile
        }

        if (!result?.userProfile) {
          throw new Error('Resposta inválida ao buscar perfil em /user/self')
        }

        if (requestId !== requestRef.current) {
          return
        }

        setStableUser(result.userProfile)
      } catch (error) {
        if (requestId !== requestRef.current) {
          return
        }

        console.error(error)
        Sentry.captureException(error)
        setStableUser(null)
      } finally {
        if (requestId === requestRef.current) {
          setIsLoading(false)
        }
      }
    },
    [getToken, setStableUser, sources],
  )

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(currentUser => {
      updateAuthUser(currentUser).catch(error => {
        console.error(error)
        Sentry.captureException(error)
        setStableUser(null)
        setIsLoading(false)
      })
    })

    return () => {
      unsubscribe()
    }
  }, [setStableUser, updateAuthUser])

  useEffect(() => {
    if (bypassAuth) {
      Sentry.setUser({
        id: bypassUserProfile.id,
        email: bypassUserProfile.email,
        username: bypassUserProfile.displayName ?? undefined,
      })
    } else {
      Sentry.setUser(
        user
          ? {
              id: user.id,
              email: user.email,
              username: user.displayName ?? undefined,
            }
          : null,
      )
    }
  }, [user])

  const context: Context = useMemo(() => {
    return {
      ...contextBase,
      token,
      getToken, // To refresh token
      user,
    }
  }, [getToken, token, user])

  return (
    <APIProvider
      value={{
        sources,
        context,
        isLoading,
      }}
    >
      {children}
    </APIProvider>
  )
}
